import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import DOMPurify from "dompurify";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import Seo from "@/components/seo/Seo";
import ImageLightbox from "@/components/ui/ImageLightbox";
import { getFeatureIcon } from "@/data/featureIcons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FeatureDetailPage() {
  const { slug } = useParams();
  const [feature, setFeature] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/features/${slug}`);
        if (!alive) return;
        setFeature(data);
      } catch {
        if (alive) setNotFound(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [slug]);

  // Compute here (before any early returns) so the useEffect below always
  // runs in the same order per render (Rules of Hooks).
  const cleanBody = feature ? DOMPurify.sanitize(feature.body_html || "") : "";

  // Attach click-to-zoom on every <img> inside the body (hero image is
  // rendered separately above and is intentionally NOT zoomable per client
  // request). Uses event delegation so re-renders don't leak listeners.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    el.querySelectorAll("img").forEach((img) => {
      img.style.cursor = "zoom-in";
      img.setAttribute("data-zoomable", "true");
    });
    const onClick = (e) => {
      const target = e.target;
      if (target && target.tagName === "IMG" && target.getAttribute("src")) {
        e.preventDefault();
        setLightboxSrc(target.getAttribute("src"));
      }
    };
    el.addEventListener("click", onClick);
    return () => el.removeEventListener("click", onClick);
  }, [cleanBody]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="feature-detail-loading">
        <p className="text-sm text-slate-400">Loading…</p>
      </div>
    );
  }

  if (notFound || !feature) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4" data-testid="feature-detail-not-found">
        <p className="text-lg font-semibold text-slate-900">This feature page isn&apos;t published yet.</p>
        <p className="text-sm text-slate-500 mt-2">Head back to the full feature list.</p>
        <Link to="/features" className="mt-6">
          <Button className="bg-sky-600 hover:bg-sky-700 text-white rounded-full h-10 px-6 text-sm font-semibold">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Features
          </Button>
        </Link>
      </div>
    );
  }

  const Icon = getFeatureIcon(feature.icon);

  return (
    <div data-testid="feature-detail-page" className="page-transition">
      <Seo
        title={feature.seo_title || `${feature.name} - Antera Software`}
        description={feature.seo_description || feature.short_desc}
        keywords={feature.seo_keywords}
        path={`/features/${feature.slug}`}
      />

      {/* Hero */}
      <section className="pt-28 lg:pt-36 pb-14 lg:pb-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/features"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-600 hover:text-sky-700"
            data-testid="feature-detail-back"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            All Features
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="mt-6 flex items-start gap-4">
              <span className="h-14 w-14 shrink-0 rounded-2xl bg-sky-50 text-sky-600 flex items-center justify-center">
                <Icon className="h-6 w-6" />
              </span>
              <div>
                <h1
                  className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900"
                  data-testid="feature-detail-title"
                >
                  {feature.name}
                </h1>
                {feature.short_desc && (
                  <p className="mt-3 text-base lg:text-lg text-slate-600 leading-relaxed max-w-2xl">
                    {feature.short_desc}
                  </p>
                )}
              </div>
            </div>
          </motion.div>

          {feature.hero_image && (
            <div className="mt-10 rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
              <img
                src={feature.hero_image}
                alt={feature.name}
                className="w-full h-auto object-cover"
                data-testid="feature-detail-hero-image"
              />
            </div>
          )}
        </div>
      </section>

      {/* Body */}
      {cleanBody && (
        <section className="pb-16 lg:pb-24 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <article
              ref={bodyRef}
              data-testid="feature-detail-body"
              className="blog-article-content"
              dangerouslySetInnerHTML={{ __html: cleanBody }}
            />
          </div>
        </section>
      )}

      {/* Step-by-step walkthrough (optional; only renders when steps are present) */}
      {Array.isArray(feature.steps) && feature.steps.length > 0 && (
        <section
          data-testid="feature-detail-steps"
          className="pb-16 lg:pb-24 bg-slate-50/60"
        >
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 lg:pt-20">
            {feature.steps_heading && (
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900 text-center mb-12 lg:mb-16">
                {feature.steps_heading}
              </h2>
            )}
            <div className="space-y-14 lg:space-y-20">
              {feature.steps.map((step, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5 }}
                  className={`flex flex-col ${i % 2 === 1 ? "lg:flex-row-reverse" : "lg:flex-row"} items-center gap-8 lg:gap-14`}
                  data-testid={`feature-detail-step-${i + 1}`}
                >
                  {/* Text column */}
                  <div className="w-full lg:flex-1 lg:min-w-0">
                    <h3
                      className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900"
                      data-testid={`feature-detail-step-${i + 1}-title`}
                    >
                      {step.title}
                    </h3>
                    {step.description && (
                      <p
                        className="mt-4 text-[15px] sm:text-base text-slate-600 leading-relaxed whitespace-pre-line"
                        data-testid={`feature-detail-step-${i + 1}-desc`}
                      >
                        {step.description}
                      </p>
                    )}
                  </div>

                  {/* Image column */}
                  <div className="w-full lg:flex-1 lg:min-w-0">
                  {step.image ? (
                    <button
                      type="button"
                      onClick={() => setLightboxSrc(step.image)}
                      className="rounded-2xl overflow-hidden border border-slate-200 shadow-md hover:shadow-xl bg-white transition-all block w-full group cursor-zoom-in"
                      data-testid={`feature-detail-step-${i + 1}-image-btn`}
                      aria-label={`View ${step.title} screenshot`}
                    >
                      <img
                        src={step.image}
                        alt={step.title}
                        className="w-full h-auto object-cover group-hover:scale-[1.02] transition-transform duration-500"
                        loading="lazy"
                        data-testid={`feature-detail-step-${i + 1}-image`}
                      />
                    </button>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-white/50 aspect-[16/10] flex items-center justify-center text-slate-400 text-sm">
                      No image yet
                    </div>
                  )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-sky-600 py-14 lg:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">
            See {feature.name} in your business.
          </h2>
          <p className="mt-3 text-sm text-sky-100">
            A personalized demo built around your workflows, your suppliers, and your business.
          </p>
          <Link to="/contact" className="inline-block mt-6">
            <Button className="bg-white text-sky-700 hover:bg-sky-50 rounded-full px-8 h-12 text-sm font-semibold">
              Request a Demo <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <ImageLightbox
        src={lightboxSrc}
        alt={feature.name}
        onClose={() => setLightboxSrc(null)}
      />
    </div>
  );
}
